import { useEffect, useMemo, useRef, useState } from 'react';
import { BookUser, Loader2, Search, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  useReactNativeBridge,
  useReactNativeMessageHandler,
  type RNMessage,
} from '@/hooks/useReactNativeBridge';
import {
  contactInitials,
  contactMatchesQuery,
  formatContactPhone,
  loadWebContacts,
  parseDeviceContacts,
  webContactsUnsupportedMessage,
  type DeviceContact,
} from '@/lib/device-contact';

const VISIBLE_LIMIT = 150;

interface ContactPickerDialogProps {
  mode: 'native' | 'web';
  initialContacts: DeviceContact[];
  initialError: string | null;
  onOpenChange: (open: boolean) => void;
  onSelect: (contact: DeviceContact) => void;
}

interface ContactLoadError {
  message: string;
  canOpenSettings: boolean;
}

export function ContactPickerDialog({
  mode,
  initialContacts,
  initialError,
  onOpenChange,
  onSelect,
}: ContactPickerDialogProps) {
  const { requestDeviceContacts, openAppSettings } = useReactNativeBridge();
  const [contacts, setContacts] = useState<DeviceContact[]>(initialContacts);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(mode === 'native');
  const [limited, setLimited] = useState(false);
  const [error, setError] = useState<ContactLoadError | null>(
    initialError ? { message: initialError, canOpenSettings: false } : null
  );
  const [attempt, setAttempt] = useState(0);
  const requestIdRef = useRef<string | null>(null);
  const settledRef = useRef(false);
  const contactsRef = useRef<DeviceContact[]>(initialContacts);
  const onSelectRef = useRef(onSelect);
  const onOpenChangeRef = useRef(onOpenChange);
  const onPageRef = useRef<(data: RNMessage) => void>(() => {});
  const onErrorRef = useRef<(data: RNMessage) => void>(() => {});

  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);

  useEffect(() => {
    onSelectRef.current = onSelect;
    onOpenChangeRef.current = onOpenChange;
  }, [onSelect, onOpenChange]);

  useEffect(() => {
    onPageRef.current = (data) => {
      if (data.requestId !== requestIdRef.current) return;
      const page = parseDeviceContacts(data.contacts);
      const done = data.done === true;
      setLimited(data.limited === true);

      if (data.cancelled === true && done) {
        settledRef.current = true;
        setLoading(false);
        onOpenChangeRef.current(false);
        return;
      }

      if (page.length > 0 || done) setError(null);

      let nextContacts = contactsRef.current;
      if (page.length > 0) {
        const seen = new Set(nextContacts.map((contact) => contact.id));
        nextContacts = nextContacts.slice();
        for (const contact of page) {
          if (seen.has(contact.id)) continue;
          seen.add(contact.id);
          nextContacts.push(contact);
        }
        contactsRef.current = nextContacts;
        setContacts(nextContacts);
      }

      if (done) {
        settledRef.current = true;
        setLoading(false);
        if (nextContacts.length === 1) {
          onSelectRef.current(nextContacts[0]);
        } else if (nextContacts.length === 0) {
          setError({
            message: 'No phone numbers found for that contact.',
            canOpenSettings: false,
          });
        }
      }
    };

    onErrorRef.current = (data) => {
      if (data.requestId !== requestIdRef.current) return;
      settledRef.current = true;
      setLoading(false);
      setContacts([]);
      setError({
        message:
          typeof data.message === 'string' && data.message.trim()
            ? data.message
            : 'Could not read contacts on this device.',
        canOpenSettings: data.canOpenSettings === true,
      });
    };
  }, []);

  const handlers = useMemo(
    () => ({
      DEVICE_CONTACTS_PAGE: (data: RNMessage) => onPageRef.current(data),
      DEVICE_CONTACTS_ERROR: (data: RNMessage) => onErrorRef.current(data),
    }),
    []
  );

  useReactNativeMessageHandler({ handlers });

  const retryWebContacts = async () => {
    setLoading(true);
    setError(null);
    setContacts([]);
    const result = await loadWebContacts();
    if (result.status === 'cancelled') {
      setLoading(false);
      return;
    }
    if (result.status === 'ok') {
      if (result.contacts.length === 1) {
        onSelect(result.contacts[0]);
        return;
      }
      setContacts(result.contacts);
      setLoading(false);
      return;
    }
    setLoading(false);
    setError({
      message: result.status === 'unsupported' ? webContactsUnsupportedMessage() : result.message,
      canOpenSettings: false,
    });
  };

  useEffect(() => {
    if (mode !== 'native') return;

    if (!window.ReactNativeWebView) {
      settledRef.current = true;
      setLoading(false);
      setError({
        message: webContactsUnsupportedMessage(),
        canOpenSettings: false,
      });
      return;
    }

    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    requestIdRef.current = requestId;
    settledRef.current = false;
    setLoading(true);
    setError(null);
    setContacts([]);
    contactsRef.current = [];
    requestDeviceContacts(requestId, { multiple: false });

    const timeout = window.setTimeout(() => {
      if (requestIdRef.current !== requestId || settledRef.current) return;
      settledRef.current = true;
      setLoading(false);
      setError({
        message: 'Could not load contacts. Try again.',
        canOpenSettings: false,
      });
    }, 60000);

    return () => window.clearTimeout(timeout);
  }, [attempt, mode, requestDeviceContacts]);

  const filtered = useMemo(
    () => contacts.filter((contact) => contactMatchesQuery(contact, query)),
    [contacts, query]
  );
  const visible = filtered.slice(0, VISIBLE_LIMIT);
  const hiddenCount = filtered.length - visible.length;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(85dvh,40rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <div className="space-y-4 pb-4 pl-5 pr-12 pt-5">
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle className="font-heading text-xl font-bold">Select Contact</DialogTitle>
            <DialogDescription>
              Pick a contact to fill in their name and phone number.
            </DialogDescription>
          </DialogHeader>
          <Input
            icon={<Search className="h-4 w-4" />}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by name or number..."
            aria-label="Search contacts by name or number"
            disabled={loading && contacts.length === 0}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto bg-muted/40 px-4 pb-4">
          {loading && contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-sm">Reading contacts from this device...</p>
            </div>
          ) : error && contacts.length === 0 ? (
            <div className="flex flex-col items-center gap-4 px-4 py-14 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <BookUser className="h-6 w-6" />
              </div>
              <p className="text-sm text-muted-foreground">{error.message}</p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (mode === 'web') {
                      void retryWebContacts();
                      return;
                    }
                    settledRef.current = false;
                    setContacts([]);
                    setQuery('');
                    setError(null);
                    setLimited(false);
                    setLoading(true);
                    setAttempt((value) => value + 1);
                  }}
                >
                  Try again
                </Button>
                {error.canOpenSettings && (
                  <Button type="button" className="gap-2" onClick={openAppSettings}>
                    <Settings className="h-4 w-4" />
                    Open Settings
                  </Button>
                )}
              </div>
            </div>
          ) : visible.length === 0 ? (
            <div className="px-4 py-14 text-center text-sm text-muted-foreground">
              {contacts.length === 0
                ? limited
                  ? 'No contacts were shared with the app. Allow access to more contacts in Settings.'
                  : 'No phone numbers found on this device.'
                : 'No contacts match your search.'}
              {contacts.length === 0 && limited && (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-4 gap-2"
                  onClick={openAppSettings}
                >
                  <Settings className="h-4 w-4" />
                  Open Settings
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {visible.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => onSelect(contact)}
                  className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-3 py-3 text-left text-card-foreground shadow-sm transition hover:border-primary/40 hover:shadow-md active:scale-[0.99]"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 text-sm font-semibold text-white">
                    {contactInitials(contact.name)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-semibold text-card-foreground">
                      {contact.name}
                    </span>
                    <span className="block truncate text-sm text-muted-foreground">
                      {formatContactPhone(contact.phone, contact.countryCode)}
                    </span>
                  </span>
                </button>
              ))}
              {hiddenCount > 0 && (
                <p className="px-1 py-2 text-center text-xs text-muted-foreground">
                  Showing the first {VISIBLE_LIMIT}. Search to narrow the list.
                </p>
              )}
              {loading && (
                <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading more contacts...
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
